import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ConfigService } from 'src/app/core/config/config-service';
import { Integration, IntegrationWrite } from '../../../shared/models/integration.model';
import { FilterUtilsService } from 'src/app/features/common-data/services/filter-utils.service';
import { IntegrationMetrics } from 'src/app/shared/models/integration-metrics.model';
import { LookupOption } from 'src/app/shared/models/lookup-option.model';
import { DeliveryNoteOption, InvoiceOption, OrderOption } from 'src/app/shared/models/interface-options';
import { TableQueryParams } from '../../../shared/models/table-query-params.model';
import { IntegrationVendorDashboard } from '../../../shared/models/integration-vendor-dashboard.model';
import { IntegrationFtpSettings, IntegrationFtpSettingsWrite } from '../../../shared/models/integration-ftp-settings';
import { CostCenter, resolveCurrentRelation } from 'src/app/shared/models/costcenter.model';
import { RequestStore } from '../stores/request.store';

@Injectable({
  providedIn: 'root'
})
export class IntegrationService {

  constructor(private http: HttpClient, private configService: ConfigService, private requestStore: RequestStore) { }

  private filterUtilService = inject(FilterUtilsService);


  private get baseUrl(): string {
    return `${this.configService.getUrlVendorCatalogApi()}/integrations`;
  }

    private get baseUrlFtp(): string {
    return `${this.configService.getUrlVendorCatalogApi()}/ftp`;
  }


  fetchIntegrationsByVendorId(vendorId: number): Observable<Integration[]> {
    return this.http.get<Integration[]>(`${this.baseUrl}/vendor/${vendorId}`);
  }

  fetchIntegrationById(integrationId: number, includeHistory = false): Observable<Integration> {
    let params = new HttpParams().set('includeHistory', includeHistory.toString());

    return this.http.get<Integration>(`${this.baseUrl}/${integrationId}`, { params }).pipe(
      map(integration => {
        if (integration.costCenters)
          for (var costCenter of integration.costCenters) {
            costCenter.selectedInterfaceOptions = this.mapInterfaceOptions(costCenter, integration.id);
          }
        return integration;
      })
    );
  }

  fetchPagedIntegrations(params: TableQueryParams): Observable<{ items: IntegrationVendorDashboard[]; totalCount: number }> {

     var filters = params.filters ? this.filterUtilService.mapFiltersForApi(params.filters): null;

     let httpParams = new HttpParams()
    .set('page', params.page)
    .set('size', params.size)
    .set('sort', (params.sort ?? '').toString())
    .set('sortDir', (params.sortOrder ?? 1).toString())
    .set('search', (params.globalFilter ?? '').toString())
    .set('filters', (JSON.stringify(filters) ?? '').toString());

    return this.http.get<{ items: IntegrationVendorDashboard[]; totalCount: number }>(`${this.baseUrl}/paged`, { params: httpParams }).pipe(
      map((result) => {
        for(var integration of result.items) {
          const allOptions: LookupOption<string>[] = [];

          for (const costCenter of integration.costCenters) {
            const options = this.mapInterfaceOptions(costCenter, integration.id);
            allOptions.push(...options);
          }

          // Entferne Duplikate anhand von `value`
          const integrationCostCenterRelationOptions = allOptions.filter(
            (option, index, self) =>
              index === self.findIndex(o => o.value === option.value)
          );

          integration.integrationCostCenterRelationOptions = integrationCostCenterRelationOptions;
        }
 
        return result;
      })
    );
  }

  fetchFtpWhitelist(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrlFtp}/whitelist`,);
  }

  fetchMetrics(clientId?: number | null): Observable<IntegrationMetrics[]> {
    let params = new HttpParams();
    if (clientId) {
      params = params.set('clientId', clientId.toString());
    }
    return this.http.get<IntegrationMetrics[]>(`${this.baseUrl}/metrics`, { params });
  }

   createIntegration(integration: IntegrationWrite): Observable<Integration> {
    return this.http.post<Integration>(`${this.baseUrl}/?createFtpCredentials=true`, integration, {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
    }).pipe(
      map(integration => {
        if (integration.costCenters)
          for (var costCenter of integration.costCenters) {
            costCenter.selectedInterfaceOptions = this.mapInterfaceOptions(costCenter, integration.id);
          }
        return integration;
      }));
  }

  updateIntegration(integrationId: number, integration: IntegrationWrite): Observable<Integration> {
    return this.http.put<Integration>(`${this.baseUrl}/${integrationId}`, integration).pipe(
      map(integration => {
        if (integration.costCenters)
          for (var costCenter of integration.costCenters) {
            costCenter.selectedInterfaceOptions = this.mapInterfaceOptions(costCenter, integration.id);
          }
        return integration;
      })
    );
  }

  updateIntegrationFtpSettings(integrationId: number, integrationFtpSettings: IntegrationFtpSettingsWrite): Observable<boolean> {
    return this.http.put<boolean>(`${this.baseUrl}/${integrationId}/ftpsettings`, integrationFtpSettings);
  }

  fetchIntegrationFtpSettingsByIntegrationId(integrationId: number): Observable<IntegrationFtpSettings> {
    return this.http.get<IntegrationFtpSettings>(`${this.baseUrl}/${integrationId}/ftpsettings`);
  }

  deleteIntegration(integrationId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${integrationId}`);
  }

  isCostCenterRelationDeletable(integrationId: number, costCenterId: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.baseUrl}/${integrationId}/cost-center-relation/${costCenterId}/deletable`);
  }

  /**
   * Ermittelt die Schnittstellen-Chips (Bestellungen / Lieferscheine / Rechnungen) einer
   * Kostenstelle für genau eine Anbindung.
   *
   * Eine Kostenstelle ist mit je einer Relation pro Lieferanten-Anbindung verknüpft. Die
   * Relation wird deshalb über die `integrationId` gesucht statt über den Index 0 — liefert
   * eine API-Antwort versehentlich fremde Relationen mit, werden hier keine Chips angezeigt
   * statt falscher.
   */
  private mapInterfaceOptions(costCenter: CostCenter, integrationId?: number): LookupOption<string>[] {
    var options: LookupOption<string>[] = []

    // Löst gleichzeitig `costCenter.currentRelation` auf, das Komponenten und Templates
    // anstelle von `integrationCostCenterRelations[0]` verwenden.
    const relation = resolveCurrentRelation(costCenter, integrationId);

    if (!relation)
      return options;

    if (relation.isOrder)
      options.push(OrderOption);

    if (relation.isDeliveryNote)
      options.push(DeliveryNoteOption);

    if (relation.isInvoice)
      options.push(InvoiceOption);

    return options;
  }

  /* Prüft ob die Integration gelöscht (true) oder nur deaktiviert werden darf (false) */
  checkIntegrationDeletable(integration: IntegrationVendorDashboard | Integration): boolean | null {
  // Falls keine ID vorhanden ist, können wir keine Requests prüfen -> Kann eigentlich nicht sein
  if (!integration?.id) {
    return null;
  }
      // Klammern nötig: `>` bindet stärker als `??`, ohne sie war der Ausdruck
      // `closedRequestCount ?? (0 > 0)` und hatte den Typ `number | false`.
      const hasClosed = (integration?.closedRequestCount ?? 0) > 0;
      const hasCostCenter = !!integration?.costCenters?.length;

      // TODO: Eigetlich kann ich die Funktion nach aktueller Anforderung zusammenfassen, 
      // damit egal ob Listenpreis oder kundenspezifischer Preis immer nur auf hasClosed geprüft wird.
      // Aber für den Fall, dass sich die Anforderungen ändern, lasse ich die Unterscheidung erstmal drin.

      // Variante 1: Listenpreise
      if (integration.isListPrice) {
        // - Kein Betrieb enthalten UND keine geschlossene Anfrage -> true
        if (!hasCostCenter && !hasClosed) {
          return true;
        }
        // - Ein Betrieb enthalten UND es existiert eine geschlossene Anfrage -> false
        if (hasCostCenter && hasClosed) {
          return false;
        }

        // - Ein Betrieb enthalten UND es existiert keine geschlossene Anfrage -> true
        if (hasCostCenter && !hasClosed) {
          return true;
        }

        // - Ein Betrieb enthalten UND es existiert keine geschlossene Anfrage -> true
        if (!hasCostCenter && hasClosed) {
          return false;
        }
        
        // Alle übrigen Fälle -> null (TODO später präzisieren)
        return null;
      }

      // Variante 2: kundenspezifischer Preis
      // - Existiert keine geschlossene Anfrage -> true
      if (!hasClosed) {
        return true;
      }
      // - Existiert eine geschlossene Anfrage -> false
      if (hasClosed) {
        return false;
      }

      // Fallback (sollte eigentlich nicht erreicht werden)
      return null;
  
}
}
